export type CampaignActionDef = {
  type?: string;
  delay?: number;
  delayUnit?: string;
};

export type ProspectCampaignActionStep = {
  type?: string;
  done?: string;
  sentAt?: string | Date;
};

export type ProspectForPipeline = {
  _id?: string;
  full_name?: string;
  headline?: string;
  inCampaign?: boolean;
  connected?: string;
  hasReplied?: boolean;
  campaignActions?: ProspectCampaignActionStep[];
  companyDetails?: { name?: string };
};

export type PipelineDeal = {
  id: string;
  name: string;
  company: string;
  role: string;
  list: string;
  hoursAgo: number;
  stageKey: string;
  stageLabel: string;
};

export type PipelineStage = {
  key: string;
  label: string;
  dotClass: string;
  deals: PipelineDeal[];
};

const PIPELINE_DOT_CLASSES = [
  "bg-pipeline-new",
  "bg-pipeline-contacted",
  "bg-pipeline-connected",
  "bg-pipeline-replied",
  "bg-pipeline-interested",
  "bg-pipeline-won",
] as const;

const STEP_COMPLETE = new Set(["DONE", "HASREPLIED"]);

export const campaignActionPipelineLabel = (type: string | undefined, index: number) => {
  const normalized = String(type || "").trim();
  if (normalized === "invitation") return "Invite sent";
  if (normalized === "visit" || normalized === "visitWithDelay") return "Profile visit";
  if (normalized === "emailSent" || normalized === "emailSentWithDelay") return "Email";
  if (normalized === "message" || normalized === "messageWithDelay") return "Message";
  return `Step ${index + 1}`;
};

export const isOutreachCampaignAction = (action: CampaignActionDef) => {
  const type = String(action?.type || "").trim();
  if (!type) return false;
  if (type === "delay") return false;
  return true;
};

/** Indexes in campaign.actions that correspond to pipeline columns (skips delays only). */
export const getOutreachActionIndices = (campaignActions: CampaignActionDef[]) => {
  const indices: number[] = [];
  (campaignActions || []).forEach((action, index) => {
    if (isOutreachCampaignAction(action)) indices.push(index);
  });
  return indices;
};

const getRelevantProspectSteps = (
  prospect: ProspectForPipeline,
  campaignActions: CampaignActionDef[]
): ProspectCampaignActionStep[] => {
  const steps = prospect.campaignActions || [];
  return getOutreachActionIndices(campaignActions).map((index) => steps[index] ?? {});
};

/** Same rule as CampaignViewDetails connectionsSent. */
export const isInvitationStepDone = (
  prospect: ProspectForPipeline,
  campaignActions: CampaignActionDef[]
) => {
  const steps = prospect.campaignActions || [];
  return (campaignActions || []).some(
    (action, index) =>
      String(action?.type || "").trim() === "invitation" &&
      isStepComplete(steps[index]?.done)
  );
};

export const findInviteOutreachStepIndex = (campaignActions: CampaignActionDef[]) => {
  const indices = getOutreachActionIndices(campaignActions);
  return indices.findIndex((actionIndex) => {
    const type = String((campaignActions || [])[actionIndex]?.type || "").trim();
    return type === "invitation";
  });
};

export const campaignHasInvitationStep = (campaignActions: CampaignActionDef[]) =>
  (campaignActions || []).some((action) => String(action?.type || "").trim() === "invitation");

/** Pending / Accepted columns — aligned with CampaignViewDetails connection stats. */
export const resolveConnectionPipelineStageKey = (
  prospect: ProspectForPipeline,
  campaignActions: CampaignActionDef[]
): "pending" | "accepted" | null => {
  if (!campaignHasInvitationStep(campaignActions)) return null;
  if (!isInvitationStepDone(prospect, campaignActions)) return null;

  const connected = String(prospect.connected || "").trim().toUpperCase();
  if (connected === "DECLINED") return null;
  if (connected === "ACCEPTED") return "accepted";
  return "pending";
};

export const buildPipelineStageDefs = (campaignActions: CampaignActionDef[]) => {
  const outreachIndices = getOutreachActionIndices(campaignActions);
  const steps = outreachIndices.map((index) => (campaignActions || [])[index]).filter(Boolean);
  const stages: Array<{ key: string; label: string; dotClass: string; stepIndex: number | null }> =
    [
      {
        key: "new",
        label: "Prospect Name",
        dotClass: "bg-pipeline-new",
        stepIndex: null,
      },
    ];

  steps.forEach((action, index) => {
    stages.push({
      key: `step-${index}`,
      label: campaignActionPipelineLabel(action.type, index),
      dotClass: PIPELINE_DOT_CLASSES[(index + 1) % PIPELINE_DOT_CLASSES.length],
      stepIndex: index,
    });
  });

  if (campaignHasInvitationStep(campaignActions)) {
    stages.push(
      {
        key: "pending",
        label: "Pending",
        dotClass: "bg-pipeline-replied",
        stepIndex: null,
      },
      {
        key: "accepted",
        label: "Accepted",
        dotClass: "bg-pipeline-interested",
        stepIndex: null,
      }
    );
  }

  stages.push({
    key: "completed",
    label: "Completed",
    dotClass: "bg-pipeline-connected",
    stepIndex: null,
  });

  return { stages, stepCount: steps.length };
};

const isStepComplete = (done: string | undefined) =>
  STEP_COMPLETE.has(String(done || "NOT_STARTED").trim());

const hoursSince = (value: string | Date | undefined) => {
  if (!value) return 0;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60)));
};

const latestActivityAt = (steps: ProspectCampaignActionStep[] | undefined) => {
  if (!Array.isArray(steps)) return undefined;
  let latest: Date | undefined;
  for (const step of steps) {
    if (!step?.sentAt) continue;
    const date = new Date(step.sentAt);
    if (Number.isNaN(date.getTime())) continue;
    if (!latest || date.getTime() > latest.getTime()) latest = date;
  }
  return latest;
};

export const resolveProspectPipelineStageKey = (
  prospect: ProspectForPipeline,
  campaignActions: CampaignActionDef[]
): "new" | "completed" | `step-${number}` => {
  if (prospect.inCampaign === false) return "new";
  if (String(prospect.connected || "").trim() === "DECLINED") return "new";

  const relevant = getRelevantProspectSteps(prospect, campaignActions);

  if (!relevant.length) return "new";

  const allNotStarted = relevant.every((step) => {
    const done = String(step?.done || "NOT_STARTED").trim();
    return done === "NOT_STARTED";
  });
  if (allNotStarted) return "new";

  const allComplete = relevant.every((step) => isStepComplete(step?.done));
  if (allComplete) return "completed";

  // Furthest completed outreach step (gaps allowed — invite can be DONE before visit, etc.).
  let lastCompleteIndex = -1;
  for (let index = 0; index < relevant.length; index++) {
    if (isStepComplete(relevant[index]?.done)) {
      lastCompleteIndex = index;
    }
  }

  if (lastCompleteIndex === -1) return "new";
  return `step-${lastCompleteIndex}`;
};

export const bucketProspectsIntoPipelineStages = (
  prospects: ProspectForPipeline[],
  campaignActions: CampaignActionDef[],
  listLabel: string
): PipelineStage[] => {
  const { stages } = buildPipelineStageDefs(campaignActions);
  const stageMap = new Map<string, PipelineStage>();

  for (const def of stages) {
    stageMap.set(def.key, {
      key: def.key,
      label: def.label,
      dotClass: def.dotClass,
      deals: [],
    });
  }

  const buildDeal = (
    prospect: ProspectForPipeline,
    stageKey: string,
    stageLabel: string,
    idSuffix = ""
  ): PipelineDeal => {
    const name = String(prospect.full_name || "Unknown prospect").trim();
    const company =
      String(prospect.companyDetails?.name || "").trim() ||
      String(prospect.headline || "").trim() ||
      "—";
    const role = String(prospect.headline || "").trim() || "—";
    const activityAt = latestActivityAt(prospect.campaignActions);
    const baseId = String(prospect._id || name);

    return {
      id: idSuffix ? `${baseId}-${idSuffix}` : baseId,
      name,
      company,
      role,
      list: listLabel,
      hoursAgo: hoursSince(activityAt),
      stageKey,
      stageLabel,
    };
  };

  const rosterLabel = stageMap.get("new")?.label || "Prospect Name";
  const inviteOutreachIndex = findInviteOutreachStepIndex(campaignActions);
  const inviteStepKey =
    inviteOutreachIndex >= 0 ? (`step-${inviteOutreachIndex}` as const) : null;

  for (const prospect of prospects) {
    if (!prospect) continue;

    const progressKey = resolveProspectPipelineStageKey(prospect, campaignActions);

    const rosterStage = stageMap.get("new");
    if (rosterStage) {
      rosterStage.deals.push(buildDeal(prospect, "new", rosterLabel));
    }

    const progressKeys = new Set<string>();
    if (progressKey !== "new") progressKeys.add(progressKey);
    if (inviteStepKey && isInvitationStepDone(prospect, campaignActions)) {
      progressKeys.add(inviteStepKey);
    }

    const connectionKey = resolveConnectionPipelineStageKey(prospect, campaignActions);
    if (connectionKey) progressKeys.add(connectionKey);

    for (const key of progressKeys) {
      const progressStage = stageMap.get(key);
      if (!progressStage) continue;
      progressStage.deals.push(
        buildDeal(
          prospect,
          key,
          progressStage.label,
          progressKeys.size > 1 ? key : ""
        )
      );
    }
  }

  return stages.map((def) => stageMap.get(def.key)!);
};
